import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Badge, Button } from '@components';
import { Agent } from '../types';

interface AgentTableProps {
  agents: Agent[];
  onEdit: (agent: Agent) => void;
  onToggleStatus: (agent: Agent) => void;
  onDelete: (agent: Agent) => void;
  onCreateAgent?: () => void;
}

export const AgentTable: React.FC<AgentTableProps> = ({
  agents,
  onEdit,
  onToggleStatus,
  onDelete,
  onCreateAgent,
}) => {
  const { t } = useTranslation();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="success" size="sm">
            {t('agents.active')}
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary" size="sm">
            {t('agents.inactive')}
          </Badge>
        );
      case 'paused':
        return (
          <Badge variant="warning" size="sm">
            {t('agents.paused')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {status}
          </Badge>
        );
    }
  };

  const getFeatureBadges = (agent: Agent) => {
    const features = [];

    if (agent.voice) features.push('Voice');
    if (agent.web_search_enabled) features.push('Web Search');
    if (agent.live_api_calls_enabled) features.push('API Calls');
    if (agent.knowledge_base) features.push('RAG');
    if (agent.follow_up_enabled) features.push('Follow-up');

    return features.map((feature, index) => (
      <Badge key={index} variant="info" size="sm" className="mr-1 mb-1">
        {feature}
      </Badge>
    ));
  };

  const formatLastUpdated = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const getConversationCount = (agent: Agent) => {
    const count = agent.conversations_today_count ?? 0;
    return `${count} today`;
  };

  return (
    <Card>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('agents.agentName')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('agents.voice')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('agents.features')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.status')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('agents.conversations')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('agents.lastUpdated')}
                </th>
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12">
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                          smart_toy
                        </span>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {t('agents.noAgentsFound')}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                          {t('agents.noAgentsFoundDescription')}
                        </p>
                      </div>
                      <div className="pt-2">
                        <Button
                          variant="gradient-multi"
                          size="sm"
                          onClick={onCreateAgent}
                          className="inline-flex items-center gap-2"
                        >
                          <span className="material-icons text-sm">add</span>
                          {t('agents.createYourFirstAgent')}
                        </Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                agents.map(agent => (
                  <tr
                    key={agent.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-2 px-3">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {agent.name}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {agent.intro_message || 'No description available'}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {agent.voice
                        ? `${agent.voice.name} (${t(`agents.voiceType${agent.voice.voice_type.charAt(0).toUpperCase()}${agent.voice.voice_type.slice(1)}`) || agent.voice.voice_type_display})`
                        : 'Not Defined'}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">{getFeatureBadges(agent)}</div>
                    </td>
                    <td className="py-2 px-3">{getStatusBadge(agent.status)}</td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {getConversationCount(agent)}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                      {formatLastUpdated(agent.modified)}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(agent)}
                          className="p-1 h-8 w-8"
                          title="Edit Agent"
                        >
                          <span className="material-icons text-lg">edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onToggleStatus(agent)}
                          className="p-1 h-8 w-8"
                          title={agent.status === 'active' ? 'Stop Agent' : 'Start Agent'}
                        >
                          <span
                            className={`material-icons text-lg ${
                              agent.status === 'active' ? 'text-red-600' : 'text-green-600'
                            }`}
                          >
                            {agent.status === 'active' ? 'stop_circle' : 'play_arrow'}
                          </span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(agent)}
                          className="p-1 h-8 w-8"
                          title="Delete Agent"
                        >
                          <span className="material-icons text-red-600 text-lg">delete</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
